import { searchMachines } from '@internal/services/apiClient';
import { Machine } from '@internal/utils/machine/Machine';
import { MachineAttribute } from '@internal/utils/machine/MachineAttribute';
import { SortOrder } from '@internal/utils/search/SortOrder';
import { TSearchFilter } from '@internal/utils/search/TSearchFilter';
import { TSearchFilterBoolean } from '@internal/utils/search/TSearchFilterBoolean';
import { TSearchFilterEnumList } from '@internal/utils/search/TSearchFilterEnumList';
import { TSearchSortOption } from '@internal/utils/search/TSearchSortOption';
import { useUserCountryCodeForSearch } from '@internal/utils/user/useUserCountryCodeForSearch';
import { AxiosError } from 'axios';
import isEqual from 'lodash/isEqual';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TSearchResult } from 'services/TSearchResult';
import { deriveLoadType, LoadType } from './deriveLoadType';
import { loadChunkSize } from './loadChunkSize';
import { maxSearchResults } from './maxSearchResults';
import { rangeFiltersMapToSearchFilters } from './rangeFiltersMapToSearchFilters';
import { SearchResultsSortKey } from './SearchResultsSortKey';
import { TSearchParams } from './TSearchParams';
import { getHasAnySearchParams, useSearchParams } from './useSearchParams';
import { useSearchResultsCache } from './useSearchResultsCache';

const cacheKeyPrefix = 'search-results:';

export const searchOptionsMap: Record<SearchResultsSortKey, TSearchSortOption> =
    {
        [SearchResultsSortKey.RelevanceAsc]: {
            by: 'relevance',
            order: SortOrder.Descending,
        },
        [SearchResultsSortKey.PriceDesc]: {
            by: 'price',
            order: SortOrder.Descending,
        },
        [SearchResultsSortKey.PriceAsc]: {
            by: 'price',
            order: SortOrder.Ascending,
        },
        [SearchResultsSortKey.HoursAsc]: {
            by: 'hours',
            order: SortOrder.Ascending,
        },
        [SearchResultsSortKey.YearAsc]: {
            by: 'year',
            order: SortOrder.Ascending,
        },
        [SearchResultsSortKey.YearDesc]: {
            by: 'year',
            order: SortOrder.Descending,
        },
        [SearchResultsSortKey.Latest]: {
            by: 'latest',
            order: SortOrder.Descending,
        },
    };

interface ResultsState {
    isLoading: boolean;
    resultsCount: number;
    machines: Machine[];
    isServingFromCache: boolean;
    usedSearchParams: TSearchParams | null;
}

export const useSearchResults = () => {
    const { getUserCountryCode } = useUserCountryCodeForSearch();
    const searchParams = useSearchParams();
    const hasAnySearchParams = useMemo(
        () => getHasAnySearchParams(searchParams),
        [searchParams],
    );

    const { loadFromCache, saveToCache, removeFromCache } =
        useSearchResultsCache();
    const { asPath } = useRouter();
    const cacheKey = cacheKeyPrefix + asPath;

    const [state, setState] = useState<ResultsState>({
        // don't start indicating that we load something when we don't have any search params
        isLoading: hasAnySearchParams,
        resultsCount: 0,
        machines: [],
        isServingFromCache: false,
        usedSearchParams: null,
    });
    const abortController = useRef<AbortController | null>(null);

    const search = useCallback(
        async ({
            searchParams,
            hasAnySearchParams,
        }: {
            searchParams: TSearchParams;
            hasAnySearchParams: boolean;
        }) => {
            abortController.current?.abort();
            abortController.current = new AbortController();

            const {
                models,
                brands,
                machineSubType,
                baleChamber,
                headerType,
                amount,
                features,
                rangeFilters,
                locations,
                sort,
                tags,
            } = searchParams;
            const machineType = searchParams.machineType;

            if (!hasAnySearchParams) {
                return setState(prev => ({
                    ...prev,
                    isLoading: false,
                    machines: [],
                    resultsCount: 0,
                    isServingFromCache: false,
                    usedSearchParams: searchParams,
                }));
            }

            const loadType = deriveLoadType(
                state.usedSearchParams,
                searchParams,
            );
            let limit = loadChunkSize;
            let offset = 0;
            let isServingFromCache = false;
            const filters: TSearchFilter[] = [];

            // compute for incremental load
            if (loadType === LoadType.incremental) {
                limit = Math.max(loadChunkSize, amount - state.machines.length);
                offset = state.machines.length;
            } else {
                limit = Math.min(maxSearchResults, amount);
                offset = 0;
            }

            // LOCATION
            if (locations.length > 0) {
                const locationFilter: TSearchFilterEnumList = {
                    type: 'enumlist',
                    attribute: MachineAttribute.LOCATION_COUNTRY,
                    values: locations.map(location => location.toUpperCase()),
                };
                filters.push(locationFilter);
            }

            // FEATURES
            if (features.length > 0) {
                features.forEach(attribute => {
                    const featureFilter: TSearchFilterBoolean = {
                        type: 'boolean',
                        attribute,
                    };
                    filters.push(featureFilter);
                });
            }

            // RANGE FILTERS
            filters.push(...rangeFiltersMapToSearchFilters(rangeFilters));

            // BALE_CHAMBER
            if (baleChamber) {
                const baleChamberFeature: TSearchFilterEnumList = {
                    type: 'enumlist',
                    attribute: MachineAttribute.BALE_CHAMBER,
                    values: [baleChamber],
                };
                filters.push(baleChamberFeature);
            }

            // HEADER_TYPE
            if (headerType) {
                const headerTypeFeature: TSearchFilterEnumList = {
                    type: 'enumlist',
                    attribute: MachineAttribute.HEADER_TYPE,
                    values: [headerType],
                };
                filters.push(headerTypeFeature);
            }

            setState(currentState => {
                let machinesFromCache: Machine[] | null = null;
                let resultsCount = currentState.resultsCount;

                // try to serve full loads from cache
                if (loadType === LoadType.full) {
                    const cacheData = loadFromCache<TSearchResult>(cacheKey);
                    if (cacheData) {
                        machinesFromCache = cacheData.results;
                        resultsCount = cacheData.count;
                    }
                }

                isServingFromCache = !!machinesFromCache;
                return {
                    ...currentState,
                    machines: machinesFromCache ?? currentState.machines,
                    resultsCount,
                    isLoading: true,
                    isServingFromCache,
                    usedSearchParams: searchParams,
                };
            });

            try {
                const response = await searchMachines({
                    search: {
                        machineType,
                        machineSubType,
                        brands,
                        models,
                        sort: searchOptionsMap[sort],
                        limit,
                        offset,
                        filters,
                        tags,
                    },
                    abortSignal: abortController.current?.signal,
                    countryCode: await getUserCountryCode(),
                });

                setState(currentState => {
                    const resetMachines =
                        loadType === LoadType.full ||
                        currentState.isServingFromCache;
                    const existingMachines: Machine[] = resetMachines
                        ? []
                        : currentState.machines;
                    const machines = [...existingMachines, ...response.results];

                    if (!machines.length) {
                        removeFromCache(cacheKey);
                    } else {
                        saveToCache<TSearchResult>(cacheKey, {
                            ...response,
                            results: machines,
                        });
                    }

                    return {
                        ...currentState,
                        isLoading: false,
                        machines,
                        resultsCount: response.count,
                        isServingFromCache: false,
                    };
                });
            } catch (err: any) {
                if (err?.response) {
                    setState(currentState => ({
                        ...currentState,
                        isLoading: false,
                    }));
                }
                return setState(currentState => ({
                    ...currentState,
                }));
            }
        },
        [
            state.usedSearchParams,
            state.machines.length,
            loadFromCache,
            cacheKey,
            getUserCountryCode,
            removeFromCache,
            saveToCache,
        ],
    );

    useEffect(() => {
        if (!isEqual(searchParams, state.usedSearchParams)) {
            search({ searchParams, hasAnySearchParams });
        }
    }, [search, searchParams, state, hasAnySearchParams]);

    return state;
};
